import { useState } from "react";
import { useDispatch } from "react-redux";
import type {
  Airport,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import type { RebookPlanSuccess } from "@b2bportal/air-disruption-api";
import type { TripSlice } from "@b2bportal/air-shopping-api";
import {
  DisruptionExerciseContainer,
  DisruptionExerciseFooter,
  DisruptionExercisePage,
  DisruptionExercisePageContent,
} from "@b2bportal/core-disruption";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreDisruptionComponent } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { DisruptionExerciseFlightSliceCard } from "@tenant/disruption";
import {
  DisruptionExerciseProgress,
  setDisruptionExerciseProgress,
} from "../../../../features/exercise/store/slice";
import { DisruptionExercisePageWidth } from "../../components/DisruptionExercisePage/DisruptionExercisePage";
import defaultStyles from "./DisruptionExerciseRebookSelectFlightPage.module.scss";

export interface DisruptionExerciseRebookSelectFlightPageProps {
  planResponse: RebookPlanSuccess;
  setSelectedSlice: (slice: {
    segments: Array<FlightItinerarySegment>;
  }) => void;
  airports: { [key: string]: Airport };
  onFlightSelectContinue: (props: {
    origin: string;
    destination: string;
    departure: string;
    tripSlice?: TripSlice | null;
  }) => void;
  onSupportClick: () => void;
  termsLink: string;
  delayHours: string;
  hoursString: string;
  handleRefundClick: () => void;
  disruptionProductName: string;
  cancelFlowAction?: () => void;
}

export const DisruptionExerciseRebookSelectFlightPage = ({
  planResponse,
  setSelectedSlice,
  airports,
  onFlightSelectContinue,
  onSupportClick,
  termsLink,
  delayHours,
  hoursString,
  handleRefundClick,
  disruptionProductName,
  cancelFlowAction,
}: DisruptionExerciseRebookSelectFlightPageProps) => {
  const { t } = useI18nContext();
  const dispatch = useDispatch();
  const [selectedSliceIndex, setSelectedSliceIndex] = useState<number | null>(
    null
  );
  const COMPONENT_KEY =
    CoreDisruptionComponent.DisruptionExerciseRebookSelectFlightPage;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { isDesktopAndUp } = useDeviceType();

  const handleCta = () => {
    if (selectedSliceIndex != null) {
      const selectedSlice = planResponse.slices[selectedSliceIndex];
      if (selectedSlice.segments.length === 1) {
        // if 1 segment (no connections) -> search flights
        const segment = selectedSlice.segments[0];

        if (segment != null) {
          const origin = segment.origin.locationCode;
          const departure =
            segment.updatedDeparture ?? segment.scheduledDeparture;
          const destination = segment.destination.locationCode;

          onFlightSelectContinue({
            origin: origin,
            destination: destination,
            departure: departure,
            tripSlice: null,
          });

          dispatch(
            setDisruptionExerciseProgress(
              DisruptionExerciseProgress.FlightSearch
            )
          );
        }
      } else {
        // if more than 1 segment (connections) -> select segment
        dispatch(
          setDisruptionExerciseProgress(
            DisruptionExerciseProgress.SegmentSelect
          )
        );
      }
    }
  };

  const handleGoBack = () => {
    dispatch(
      setDisruptionExerciseProgress(DisruptionExerciseProgress.RebookReview)
    );
  };

  const rows = planResponse.slices.map((slice, index) => {
    return (
      <DisruptionExerciseFlightSliceCard
        isReturn={index > 0}
        isSelected={selectedSliceIndex === index}
        slice={slice}
        onSelect={() => {
          setSelectedSlice(slice);
          setSelectedSliceIndex(index);
        }}
        airports={airports}
        key={index}
      ></DisruptionExerciseFlightSliceCard>
    );
  });
  const selectableRows = <div className={cn("rowsContainer")}>{rows}</div>;

  return (
    <DisruptionExercisePage
      className={cn("DisruptionExerciseRebookSelectFlightPage")}
      pageWidth={DisruptionExercisePageWidth.Regular}
      title={
        !isDesktopAndUp
          ? disruptionProductName
          : t(
              "core-disruption.disruptionUniversalExercise.rebook.sliceSelectionPage.title"
            )
      }
      onBack={handleGoBack}
      submitButtonTitle={t(
        "core-disruption.disruptionUniversalExercise.continue"
      )}
      submitButtonAction={handleCta}
      submitButtonDisabled={selectedSliceIndex === null}
      delayHours={delayHours}
      numberOfHours={hoursString}
      termsLink={termsLink}
      onCancelFlow={cancelFlowAction}
      content={
        <DisruptionExercisePageContent
          leftContent={
            <DisruptionExerciseContainer
              content={[
                !isDesktopAndUp ? (
                  <span className={cn("title")}>
                    {t(
                      "core-disruption.disruptionUniversalExercise.rebook.sliceSelectionPage.title"
                    )}
                  </span>
                ) : (
                  <div></div>
                ),
                selectableRows,
                <DisruptionExerciseFooter
                  onSupportClick={onSupportClick}
                  onRefundClick={handleRefundClick}
                ></DisruptionExerciseFooter>,
              ]}
            ></DisruptionExerciseContainer>
          }
        />
      }
    ></DisruptionExercisePage>
  );
};

export default DisruptionExerciseRebookSelectFlightPage;
